import React, {useMemo} from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage, useIntl} from 'react-intl'
import {Box, Button, HStack, Text, VStack} from '@chakra-ui/react3'
import BVProductRating from '../../../../components/bazaarvoice/rating'
import {hasBVProductRating} from '../../../../components/bazaarvoice/utils'
import ProductDisplayPrice from '../../../../components/product-display-price'
import useAR from '../../../../hooks/use-ar'
import {useConfiguratorHighlights} from '../../../../hooks/useScapiCustomApi'
import {
    _3DViewIcon,
    PaintBoardIcon,
    ResourcesAddIcon,
    TextCircleIcon
} from '../../../../leap/components/icons'
import {getMasterProductId, isProductSet} from '../../../../utils/product-utils'
import BadgeGroup from '../../../../components/badge-group'
import CockpitVariations from '../cockpit-variations'
import CustomizablePsdpSetProductTile from '../customizable-psdp-set-product-tile'
import ProductAvailability from '../../../../leap/components/product-availability/index.jsx'
import PromotionBanner from './promotion-banner'
import Gallery from './gallery'
import DisplayPrice from '../../../../leap/components/display-price/index.jsx'
import AgeRange from '../../../../leap/components/age-range'
import CockpitCtaSection from './cockpit-cta-section'
import EstimatedDelivery from '../../../../leap/components/estimated-delivery'

const HighlightItem = ({children}) => (
    <HStack
        gap="spacing-12"
        align="center"
        color="text.primary"
        textStyle="heading.small"
        _icon={{width: '1rem', height: '1rem'}}
    >
        {children}
    </HStack>
)
HighlightItem.propTypes = {
    children: PropTypes.node
}

const XleapCockpit = ({
    derivedProduct,
    product,
    isGetPropsLoading,
    isMasterLoading,
    isPromotionsLoading,
    isVariantLoading,
    onMasterChange,
    onRatingsClick,
    viewModel,
    onShowAllColorsClick,
    onVariantChange
}) => {
    const {formatMessage} = useIntl()
    const configuratorConfig = product?.c_configuratorConfig
    const displayTemplate = product?.c_displayTemplate
    const hasProductRating = useMemo(() => hasBVProductRating(product), [product])
    const {ARModel} = derivedProduct
    const ARProps = useAR(ARModel)
    const highlightsQuery = useConfiguratorHighlights(
        {
            parameters: {
                configId: configuratorConfig,
                productId: getMasterProductId(product) || product?.id
            }
        },
        {
            enabled: displayTemplate === 'xpdp-configurator'
        }
    )
    const hasHighlights =
        highlightsQuery.data?.numberOfColors > 0 ||
        highlightsQuery.data?.numberOfAccessories > 0 ||
        highlightsQuery.data?.hasEngraving

    return (
        <Box
            data-testid={`cockpit-${displayTemplate}`}
            display={{
                base: 'flex',
                s: 'grid'
            }}
            flexDirection="column"
            paddingTop={{base: 'spacing-24', s: 'spacing-40'}}
            paddingBottom={{base: 'spacing-24', s: 'spacing-60'}}
            paddingInline={{base: 0, s: 'spacing-92'}}
            gapY={'spacing-32'}
            gapX={{base: 'spacing-32', s: 'spacing-80'}}
            align={{base: 'stretch', s: 'start'}}
            layerStyle="container.page"
            gridTemplateAreas='"promotion promotion" "gallery details"'
            gridTemplateColumns="minmax(0, 1fr) 25rem"
            gridTemplateRows="auto auto"
        >
            <Box gridArea="promotion">
                <PromotionBanner product={product} />
            </Box>
            <Box
                gridArea="gallery"
                position="sticky"
                top="spacing-8"
                zIndex={1}
                alignSelf={{s: 'start'}}
                height="fit-content"
            >
                <Gallery product={product} derivedProduct={derivedProduct}></Gallery>
            </Box>

            <Box gridArea="details" minWidth={0}>
                <VStack gap="spacing-32" align="stretch">
                    <Box data-testid="main-info" paddingInline={{base: 'spacing-24', s: 0}}>
                        <VStack gap="spacing-20" align="stretch">
                            <VStack gap="0" align="stretch">
                                <Box>
                                    <Box float="right">
                                        <BadgeGroup product={product} />
                                    </Box>
                                    <Box>
                                        <AgeRange
                                            product={product}
                                            variant="configurator-quickview"
                                        />
                                    </Box>
                                    <Text as="h1">
                                        <Text
                                            as="span"
                                            display="inline-block"
                                            color="text.primary"
                                            textStyle="heading.xlarge"
                                        >
                                            {product.name}
                                        </Text>
                                    </Text>
                                </Box>

                                <Text
                                    as="span"
                                    display="inline-block"
                                    color="text.tertiary"
                                    textStyle="body.default"
                                >
                                    {product.c_slogan}
                                </Text>
                            </VStack>

                            {hasProductRating && (
                                <BVProductRating
                                    product={product}
                                    variant="cockpit"
                                    onRatingsClick={onRatingsClick}
                                />
                            )}
                        </VStack>
                    </Box>

                    <VStack
                        gap="spacing-24"
                        align="stretch"
                        paddingInline={{base: 'spacing-24', s: 0}}
                    >
                        {displayTemplate === 'xpdp' &&
                            (isProductSet(product) ? (
                                product.setProducts.map((setProduct) => (
                                    <CustomizablePsdpSetProductTile
                                        key={setProduct.setProductId}
                                        product={setProduct}
                                    />
                                ))
                            ) : (
                                <CockpitVariations
                                    product={product}
                                    isMasterLoading={isMasterLoading}
                                    isVariantLoading={isVariantLoading}
                                    onMasterChange={onMasterChange}
                                    onVariantChange={onVariantChange}
                                    onShowAllColorsClick={onShowAllColorsClick}
                                />
                            ))}

                        <Text color="text.secondary" textStyle="body.small">
                            {product.shortDescription}
                        </Text>

                        {displayTemplate === 'xpdp-configurator' && hasHighlights && (
                            <VStack gap="spacing-12" align="stretch">
                                {highlightsQuery.data?.numberOfColors > 0 && (
                                    <HighlightItem>
                                        <PaintBoardIcon role="presentation" />

                                        <FormattedMessage
                                            id="product_detail.number_of_colors"
                                            defaultMessage="{value} beautiful colors"
                                            values={{
                                                value: highlightsQuery.data?.numberOfColors
                                            }}
                                        />
                                    </HighlightItem>
                                )}

                                {highlightsQuery.data?.numberOfAccessories > 0 && (
                                    <HighlightItem>
                                        <ResourcesAddIcon role="presentation" />

                                        <FormattedMessage
                                            id="product_detail.number_of_accessories"
                                            defaultMessage="{value} accessories available"
                                            values={{
                                                value: highlightsQuery.data?.numberOfAccessories
                                            }}
                                        />
                                    </HighlightItem>
                                )}

                                {highlightsQuery.data?.hasEngraving && (
                                    <HighlightItem>
                                        <TextCircleIcon role="presentation" />

                                        <FormattedMessage
                                            id="product_detail.has_engraving"
                                            defaultMessage="Free engraving"
                                        />
                                    </HighlightItem>
                                )}
                            </VStack>
                        )}

                        {ARProps?.hasAR && (
                            <Button
                                as="a"
                                variant="secondaryOutline"
                                display={{base: 'inline-flex', s: 'none'}}
                                href={ARProps?.href || '#'}
                                rel="ar"
                                onClick={ARProps?.onARLinkClick}
                                aria-label={formatMessage({
                                    defaultMessage: 'Open AR view',
                                    id: 'product_detail.button.open_ar_view'
                                })}
                            >
                                <span>
                                    <FormattedMessage
                                        id="product_detail.button.view_in_space"
                                        defaultMessage="View in space"
                                    />
                                </span>
                                <_3DViewIcon role="presentation" />
                            </Button>
                        )}
                    </VStack>

                    <VStack
                        gap={{base: 'spacing-24', s: 'spacing-16'}}
                        align="stretch"
                        paddingInline={{base: 'spacing-24', s: 0}}
                        paddingBlock="spacing-24"
                        backgroundColor="primary.base.white"
                        borderTop="1px solid"
                        borderTopColor="border.subtle"
                        backdropFilter="auto"
                        backdropBlur="md"
                    >
                        <HStack justify="space-between" align="flex-end">
                            <ProductDisplayPrice
                                as={DisplayPrice}
                                product={product}
                                loading={
                                    isGetPropsLoading || isPromotionsLoading || isVariantLoading
                                }
                                variant="xpdp"
                            />
                            {!isProductSet(product) && (
                                <ProductAvailability
                                    loading={isVariantLoading}
                                    flexShrink="0"
                                    product={product}
                                />
                            )}
                        </HStack>
                        <CockpitCtaSection viewModel={viewModel} />
                        <EstimatedDelivery product={product}></EstimatedDelivery>
                    </VStack>
                </VStack>
            </Box>
        </Box>
    )
}

XleapCockpit.propTypes = {
    derivedProduct: PropTypes.object,
    product: PropTypes.object,
    isGetPropsLoading: PropTypes.bool,
    isMasterLoading: PropTypes.bool,
    isPromotionsLoading: PropTypes.bool,
    isVariantLoading: PropTypes.bool,
    onMasterChange: PropTypes.func,
    onRatingsClick: PropTypes.func,
    viewModel: PropTypes.object.isRequired,
    onShowAllColorsClick: PropTypes.func,
    onVariantChange: PropTypes.func
}

export default XleapCockpit
